import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppRole } from '../../hooks/useAppRole';
import type { AppRole } from '../../services/appRoleService';
import { EXPECTED_TENANT_ID } from '../../config/msalConfig';
import { AppSkeleton } from '../layout/AppSkeleton';
import { useEffect } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
}

/**
 * Validate that the authenticated user's token belongs to the expected Azure AD tenant.
 * Returns true if valid, false if the tenant doesn't match.
 */
function isValidTenant(account: { tenantId?: string } | null): boolean {
  if (!account || !EXPECTED_TENANT_ID) return false;
  return account.tenantId === EXPECTED_TENANT_ID;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, account } = useAuth();
  const { role } = useAppRole();

  // DEV-ONLY: when the bypass flag is on, render children without an MSAL
  // account or app-session user. The bypass already covered portalApiClient
  // and the /design routes (App.tsx); extending it here lets every protected
  // page (folders, projects, etc.) be exercised locally without a real Entra
  // login. The flag is never set in production builds, so this is inert in prod.
  const devBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

  useEffect(() => {
    if (devBypass) return; // don't stash a redirect path we'll never consume
    // Save the intended path when redirecting to login
    if (!isAuthenticated && !isLoading) {
      sessionStorage.setItem('authRedirectPath', location.pathname + location.search);
    }
  }, [isAuthenticated, isLoading, location, devBypass]);

  if (devBypass) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <AppSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Validate tenant after authentication
  if (!isValidTenant(account)) {
    console.error(
      `Tenant validation failed. Expected: ${EXPECTED_TENANT_ID}, Got: ${account?.tenantId}. Signing out.`
    );
    return <Navigate to="/login" replace state={{ error: 'unauthorized-tenant' }} />;
  }

  // Installers are confined to the install workspace; Managers can go anywhere.
  if (role === 'Installer' && requiredRole !== 'Installer' && !location.pathname.startsWith('/install')) {
    return <Navigate to="/install" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
